import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';

import {
  createAppTheme,
  DEFAULT_COLOR_MODE,
  DEFAULT_PRIMARY_COLOR,
  getThemeCssVariables,
} from './theme';

const COLOR_MODE_STORAGE_KEY = 'app-color-mode';

const ThemeModeContext = createContext({
  mode: DEFAULT_COLOR_MODE,
  toggleColorMode: () => {},
});

export const useThemeMode = () => useContext(ThemeModeContext);

const AppThemeProvider = ({ children }) => {
  const currentUser = useSelector((state) => state.user.currentUser);
  const companies = useSelector((state) => state.companies.list);
  const [mode, setMode] = useState(() => window.localStorage.getItem(COLOR_MODE_STORAGE_KEY) || DEFAULT_COLOR_MODE);

  const companyColor = useMemo(() => {
    if (!currentUser?.company) {
      return DEFAULT_PRIMARY_COLOR;
    }

    return companies.find((company) => company.id === currentUser.company)?.color || DEFAULT_PRIMARY_COLOR;
  }, [companies, currentUser]);

  const theme = useMemo(() => createAppTheme(companyColor, mode), [companyColor, mode]);

  useEffect(() => {
    const cssVariables = getThemeCssVariables(companyColor, mode);

    Object.entries(cssVariables).forEach(([name, value]) => {
      document.documentElement.style.setProperty(name, value);
    });
    document.documentElement.setAttribute('data-color-mode', mode);
    window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
  }, [companyColor, mode]);

  const contextValue = useMemo(() => ({
    mode,
    toggleColorMode: () => setMode((currentMode) => (currentMode === 'light' ? 'dark' : 'light')),
  }), [mode]);

  return (
    <ThemeModeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

AppThemeProvider.propTypes = {
  children: PropTypes.node,
};

export default AppThemeProvider;
